import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import SettingsIcon from "@mui/icons-material/Settings";
import CampbellLogo from "../../assets/images/campbelllogo.png";
import UserProfile from "./UserProfile";
import NotificationBell from "../NotificationBell";
import FullScreenLoader from "../Common/FullScreenLoader";
import SharePointSyncDialog from "../SharePointSyncDialog/SharePointSyncDialog";
import ResearchModeSettings from "../ResearchModeSettings/ResearchModeSettings";
import { getRecentSharePointFiles, verifyScannedFiles, createScannedFileRecord } from "../../services/apiService";

const SharePointIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
    {/* Top circle — dark teal */}
    <circle cx="13.5" cy="6.5" r="5" fill="#036c70" />
    {/* Bottom-right circle — medium teal */}
    <circle cx="17" cy="14" r="4" fill="#1a9ba1" opacity="0.9" />
    {/* Bottom-center circle — light cyan */}
    <circle cx="11" cy="16.5" r="4.5" fill="#37c6d0" opacity="0.85" />
    {/* Rounded square — dark teal */}
    <rect x="2" y="7" width="12" height="12" rx="2" ry="2" fill="#036c70" />
    {/* White "S" letter */}
    <text
      x="8"
      y="17"
      textAnchor="middle"
      fontSize="9"
      fontWeight="bold"
      fontFamily="'Segoe UI', Arial, sans-serif"
      fill="#ffffff"
    >S</text>
  </svg>
);

const Header = ({
  selectedItem,
  open,
  isAuthenticated,
  researchPreference,
  onSaveResearchPreference,
}) => {
  const [syncing, setSyncing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [recentFiles, setRecentFiles] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [sendingFiles, setSendingFiles] = useState({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);

  const handleSettingsOpen = (event) => {
    setSettingsAnchorEl(event.currentTarget);
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
    setSettingsAnchorEl(null);
  };
  const handleOpenSharePointHome = () => {
    const siteUrl = (process.env.REACT_APP_SHAREPOINT_SITE_URL || "").replace(/\/$/, "");
    const userEmail = localStorage.getItem("userEmail")?.toLowerCase() || "";
    const userFolder = userEmail.split("@")[0];
    // Build the user-specific folder URL:
    // <siteUrl>/Shared Documents/<userEmail>
    const folderPath = userFolder
      ? `${siteUrl}/Shared%20Documents/${encodeURIComponent(userFolder)}`
      : siteUrl;
    // console.log("Opening SharePoint folder:", folderPath);
    window.open(folderPath, "_blank", "noopener,noreferrer");
  };

  const handleSharePointSync = async () => {
    setSyncing(true);
    setFetchError(null);
    setRecentFiles([]);
    setDialogOpen(true);
    try {
      // Step 1 — fetch recent files from SharePoint (last 24 h)
      const userId = localStorage.getItem("userEmail")?.toLowerCase().split('@')[0];
      const scanWindowMinutes = process.env.REACT_APP_SHAREPOINT_SCAN_WINDOW_MINUTES;
      // console.log(`Fetching SharePoint files for user ${userId} within last ${scanWindowMinutes} minutes...`);
      const response = await getRecentSharePointFiles(userId, scanWindowMinutes);
      if (response?.error) {
        setFetchError(response.message || "Failed to fetch recent files.");
        return;
      }

      const rawFiles = Array.isArray(response)
        ? response
        : (response?.files ?? response?.value ?? []);

      if (rawFiles.length === 0) {
        setRecentFiles([]);
        return;
      }
      // console.log("rawFiles: ",rawFiles)
      // console.log(`Fetched ${rawFiles.length} files from SharePoint. Verifying availability...`);
      // Step 2 — verify which files exist in sharepoint_scanned_files table
      const fileNames = rawFiles.map((f) => f.name).filter(Boolean);
      const verifyResponse = await verifyScannedFiles(fileNames);
      // console.log("verifyResponse: ",verifyResponse)
      // console.log(`Verification complete. ${verifyResponse?.data?.length || 0} files are available in the database.`);
      // Build a Map of file name → DB record
      const dbRecordByName = new Map(
        (verifyResponse?.data ?? []).map((r) => [r.name, r])
      );

      // Enrich ALL rawFiles with processingStatus
      const enriched = rawFiles.map((f) => {
        const dbRecord = dbRecordByName.get(f.name);
        if (!dbRecord) return { ...f, processingStatus: "not_processed" };
        if ((dbRecord.status || "").toLowerCase() === "completed") return { ...f, processingStatus: "completed" };
        return { ...f, processingStatus: "in_progress" };
      });
      // console.log("enriched: ", enriched);
      setRecentFiles(enriched);
    } catch (err) {
      console.error("SharePoint sync failed:", err);
      setFetchError("An unexpected error occurred.");
    } finally {
      setSyncing(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleSendToJob = async (file) => {
    const fileKey = file.id || file.name;
    setSendingFiles((prev) => ({ ...prev, [fileKey]: "sending" }));
    const user_folder = localStorage.getItem("userEmail")?.toLowerCase();
    try {
      const payload = {
        item_id: file.id,
        name: file.name,
        drive_id: file.drive_id || "",
        size_bytes: file.size_bytes,
        web_url: file.web_url,
        download_url: file.download_url || null,
        created_at: file.created_at || null,
        modified_at: file.modified_at || null,
        created_by: file.created_by || null,
        modified_by: file.modified_by || null,
        mime_type: file.mime_type || null,
        library: file.library || "Documents",
        folder: user_folder || null,
        content_hash: null,
        // file.quick_xor_hash || null,

      };
      const response = await createScannedFileRecord(payload);
      if (response?.error) {
        setSendingFiles((prev) => ({ ...prev, [fileKey]: "error" }));
      } else {
        // Mark as in_progress once sent to job
        setSendingFiles((prev) => ({ ...prev, [fileKey]: "done" }));
        setRecentFiles((prev) =>
          prev.map((f) => (f.id === file.id || f.name === file.name) ? { ...f, processingStatus: "in_progress" } : f)
        );
      }
    } catch (err) {
      console.error("Send to job failed:", err);
      setSendingFiles((prev) => ({ ...prev, [fileKey]: "error" }));
    }
  };

  return (
    <Grid container sx={{ width: "100%", borderBottom: "1px solid", borderColor: "divider" }}>
      <Grid item xs={2}>
        <Box display="flex" alignItems="center" height="100%" paddingLeft={"20px"}>
          <img src={CampbellLogo} style={{ width: "95px" }} alt="CHIP Logo" />
        </Box>
      </Grid>

      <Grid item xs={8}>
        <Box
          display="flex"
          alignItems="center"
          height="100%"
          sx={{ marginLeft: open ? "33%" : "28%" }}
        >
          {/* eslint-disable-next-line react/prop-types */}
          {selectedItem.icon && (
            <Box sx={{ display: "flex", alignItems: "center", marginRight: "8px", color: "primary.main" }}>
              {selectedItem.icon}
            </Box>
          )}
          <Typography
            variant="h6"
            sx={{
              fontWeight: "700",
              fontSize: "20px",
              fontFamily: "'Poppins', sans-serif",
              color: "text.primary",
            }}
          >
            {/* eslint-disable-next-line react/prop-types */}
            {selectedItem.text}
          </Typography>

          <Box
            sx={{
              position: "absolute",
              right: isAuthenticated ? "15%" : "25%",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <NotificationBell isAuthenticated={isAuthenticated} />

            <Tooltip title="Go to My Folder">
              <IconButton
                onClick={handleOpenSharePointHome}
                size="large"
                sx={{ color: "action.active" }}
              >
                <SharePointIcon />
              </IconButton>
            </Tooltip>
            {/* <Tooltip title={mode === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"}>
              <IconButton onClick={toggleTheme} size="large">
                {mode === "light" ? <DarkModeIcon /> : <LightModeIcon />}
              </IconButton>
            </Tooltip> */}
            <Tooltip title="SharePoint Sync">
              <IconButton
                onClick={handleSharePointSync}
                size="large"
                disabled={syncing}
                sx={{ color: syncing ? "action.disabled" : "action.active" }}
              >
                {syncing ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <CloudSyncIcon />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Research Source Preference">
              <IconButton
                onClick={handleSettingsOpen}
                size="large"
                sx={{
                  color: "action.active",
                  backgroundColor: settingsOpen ? "rgba(24, 39, 93, 0.1)" : "transparent",
                  transition: "all 180ms ease",
                  "&:hover": {
                    backgroundColor: "rgba(24, 39, 93, 0.12)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Grid>

      <Grid
        item
        xs={2}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}
      >
        <UserProfile isAuthenticated={isAuthenticated} />
      </Grid>

      <SharePointSyncDialog
        open={dialogOpen}
        loading={syncing}
        files={recentFiles}
        error={fetchError}
        onClose={handleDialogClose}
        onRefresh={handleSharePointSync}
        onSendToJob={handleSendToJob}
        sendingFiles={sendingFiles}
      />

      <ResearchModeSettings
        open={settingsOpen}
        anchorEl={settingsAnchorEl}
        researchPreference={researchPreference}
        onClose={handleSettingsClose}
        onSaveResearchPreference={onSaveResearchPreference}
      />
    </Grid>
  );
};

Header.propTypes = {
  selectedItem: PropTypes.shape({
    icon: PropTypes.node,
    text: PropTypes.string
  }),
  open: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  researchPreference: PropTypes.shape({
    research_mode: PropTypes.string,
    conversion_source: PropTypes.string,
    effective_datasources: PropTypes.arrayOf(PropTypes.string),
  }),
  onSaveResearchPreference: PropTypes.func,
};

export default Header;
